import { makeAutoObservable, runInAction } from "mobx";

import { AttributeBranchType, type OptionType } from "@/api/admin";
import type { IBranchFull } from "@/api/admin/branches";
import type { BrandId } from "@/api/admin/brands";
import type {
  BranchDetailsType,
  BranchHoursFormType,
  BranchMediaFormType,
  BranchReservationType,
  BrandAmenitiesType,
} from "@/components/units";
import { getDefaultPhoneCode } from "@/utils/getDefaultPhoneCode";
import { parseE164 } from "@/utils/parseE164";
import { toE164 } from "@/utils/toE164";

export type Branch = {
  details: BranchDetailsType;
  businessHours: BranchHoursFormType;
  reservations: BranchReservationType;
  media: BranchMediaFormType;
  amenities: BrandAmenitiesType;
};

export class BranchStore {
  branch: Branch | null = null;
  private _details: BranchDetailsType | null = null;
  private _businessHours: BranchHoursFormType | null = null;
  private _reservations: BranchReservationType | null = null;
  private _media: BranchMediaFormType | null = null;
  private _amenities: BrandAmenitiesType | null = null;
  isInitialized = false;
  active: boolean = true;

  constructor() {
    makeAutoObservable(this);
  }

  // TODO: after full integration - check. Maybe this method should be deleted
  init(branch: Branch | null) {
    this.branch = branch;
    this.load();
    this.isInitialized = true;
  }

  initFromPayload(branch: IBranchFull) {
    runInAction(() => {
      this.branch = branch;
      this.isInitialized = true;

      const {
        referenceNumber,
        id,
        descriptionAr,
        descriptionEn,
        nameAr,
        nameEn,
        brandId,
        address,
        phone,
        openingHours,
        reservationRequired,
        reservationMethods,
        averageWaitTime,
        googlePlaceId,
        menuUrl,
        bookingUrl,
        legalEntityId,
        priceLevel,
        mediaItems,
        properties,
      } = branch;

      const parsed = parseE164(phone || "");
      const fallbackPhoneCode = getDefaultPhoneCode();

      this._details = {
        descriptionAr,
        descriptionEn,
        nameAr,
        nameEn,
        referenceNumber,
        id,
        brandId,
        phoneCode: parsed?.phoneCode ?? fallbackPhoneCode,
        phoneNumber: parsed?.phoneNumber ?? "",
        phone: "",
        address,
        menuUrl,
        bookingUrl,
        legalEntityId,
        googlePlaceId,
      };

      this._businessHours = {
        schedules: [...openingHours.schedules],
      };

      const parkingList =
        properties?.filter(
          item =>
            item.type === AttributeBranchType.AMENITY &&
            item.name.startsWith("parking")
        ) ?? [];

      const otherAmenitiesList =
        properties?.filter(
          item =>
            item.type === AttributeBranchType.AMENITY &&
            !item.name.startsWith("parking")
        ) ?? [];

      const tagsList =
        properties?.filter(item => item.type === AttributeBranchType.TAG) ?? [];

      const ambiancesList =
        properties?.filter(
          item => item.type === AttributeBranchType.AMBIANCE
        ) ?? [];

      const dressCodeList =
        properties?.filter(
          item => item.type === AttributeBranchType.DRESS_CODE
        ) ?? [];

      this._amenities = {
        amenities: otherAmenitiesList.map(item => item.id),
        parking: parkingList.map(item => item.id),
      };

      const tagsIdsList = tagsList.map(item => item.id);
      const ambiancesIdsList = ambiancesList.map(item => item.id);
      const dressCodeIdsList = dressCodeList.map(item => item.id);

      this._reservations = {
        reservationRequired,
        reservationMethods,
        averageWaitTime,
        priceLevel,
        tags: tagsIdsList,
        ambiance: ambiancesIdsList,
        dressCode: dressCodeIdsList,
      };

      this._media = {
        media: mediaItems,
      };
    });
  }

  get details(): BranchDetailsType {
    return this._details ?? this.defaultDetails;
  }

  get businessHours(): BranchHoursFormType {
    return this._businessHours ?? this.defaultBusinessHours;
  }

  get reservations(): BranchReservationType {
    const data = this._reservations ?? this.defaultReservations;

    return {
      ...data,
      ...(data?.priceLevel > 0 && {
        priceLevel: [data.priceLevel],
      }),
    };
  }

  get amenities(): BrandAmenitiesType {
    return this._amenities ?? this.defaultAmenities;
  }

  get media(): BranchMediaFormType {
    return this._media ?? this.defaultMedia;
  }

  setDetails(data: BranchDetailsType) {
    this._details = data;
  }

  setBusinessHours(data: BranchHoursFormType) {
    this._businessHours = data;
  }

  setReservations(data: BranchReservationType) {
    this._reservations = {
      ...data,
      ...(data?.priceLevel.length > 0 && {
        priceLevel: data.priceLevel[0],
      }),
    };
  }

  setAmenities(data: BrandAmenitiesType) {
    this._amenities = data;
  }

  setMedia(data: BranchMediaFormType) {
    this._media = data;
  }

  private load() {
    if (this.branch) {
      this._details = this.branch.details ?? null;
      this._businessHours = this.branch.businessHours ?? null;
      this._reservations = this.branch.reservations ?? null;
      this._amenities = this.branch.amenities ?? null;

      const rawMedia = this.branch.media;
      this._media = {
        media: rawMedia.media,
      };
    }
  }

  get defaultDetails(): BranchDetailsType {
    const fallbackPhoneCode = getDefaultPhoneCode();

    return {
      nameEn: "",
      nameAr: "",
      phoneCode: fallbackPhoneCode || "",
      phoneNumber: "",
      phone: "",
      descriptionAr: "",
      descriptionEn: "",
      address: "",
      brandId: "",
      googlePlaceId: "",
      menuUrl: "",
      bookingUrl: "",
      legalEntityId: "",
    };
  }

  get defaultBusinessHours(): BranchHoursFormType {
    return {
      schedules: [
        {
          day: "sunday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "monday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "tuesday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "wednesday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "thursday",
          closed: false,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "friday",
          closed: true,
          open: "09:00",
          close: "23:00",
        },
        {
          day: "saturday",
          closed: true,
          open: "09:00",
          close: "23:00",
        },
      ],
    };
  }

  get defaultReservations(): BranchReservationType {
    return {
      reservationMethods: [],
      averageWaitTime: "UNDER_10_MIN",
      reservationRequired: false,

      tags: [],
      ambiance: [],
      dressCode: [],
      priceLevel: 1,
    };
  }

  get defaultAmenities(): BrandAmenitiesType {
    return {
      amenities: [],
      parking: [],
    };
  }

  get defaultMedia(): BranchMediaFormType {
    return {
      media: [],
      mediaIds: [],
      unlinkProcessedMediaIds: [],
    };
  }

  get fullPayload() {
    return {
      details: this.details,
      businessHours: this.businessHours,
      reservations: this.reservations,
      amenities: this.amenities,
      media: this.media,
    };
  }

  getBranchName() {
    return this.details.nameEn;
  }

  setActive(value: boolean) {
    if (value === false) {
      this.active = false;
    }
  }

  parseTime(val: string) {
    const [hours, minutes] = val.split(":").map(Number);
    return new Date(Date.UTC(1970, 0, 1, hours, minutes));
  }

  shiftTime(val: string, offsetHours: number) {
    const date = this.parseTime(val);
    const shiftedHours = (date.getUTCHours() + offsetHours + 24) % 24;
    const minutes = date.getUTCMinutes();
    return `${String(shiftedHours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  prepareData(data: Branch, id?: BrandId) {
    const formattedNumber = toE164(
      data.details.phoneCode ?? "",
      data.details?.phoneNumber ?? ""
    );

    const price = Array.isArray(data.reservations.priceLevel)
      ? data.reservations.priceLevel[0]
      : typeof data.reservations.priceLevel === "number"
        ? data.reservations.priceLevel
        : undefined;

    const payload: IBranchFull = {
      ...(data.details.referenceNumber
        ? { referenceNumber: data.details.referenceNumber }
        : {}),
      nameAr: data.details.nameAr.trim(),
      nameEn: data.details.nameEn.trim(),
      descriptionAr: data.details?.descriptionAr?.trim(),
      descriptionEn: data.details.descriptionEn?.trim(),
      legalEntityId: data.details.legalEntityId,
      menuUrl: data.details.menuUrl,
      bookingUrl: data.details.bookingUrl,
      googlePlaceId: data.details.googlePlaceId,
      phone: formattedNumber,
      address: data.details.address,
      brandId: id ?? data.details.brandId,
      longitude: data.details.longitude,
      latitude: data.details.latitude,

      openingHours: {
        schedules: data.businessHours.schedules.map(schedule => ({
          ...schedule,
          open: this.shiftTime(schedule.open, -3),
          close: this.shiftTime(schedule.close, -3),
        })),
      },

      reservationRequired: data.reservations.reservationRequired,
      reservationMethods: [...data.reservations.reservationMethods],
      priceLevel: price,
      averageWaitTime:
        data.reservations.averageWaitTime ??
        this.defaultReservations.averageWaitTime,
      active: this.active,
      mediaIds: data.media?.mediaIds,
      unlinkProcessedMediaIds: data.media?.unlinkProcessedMediaIds,
      properties: [
        ...(data.amenities?.amenities ?? []),
        ...(data.amenities?.parking ?? []),
        ...(data.reservations?.tags ?? []),
        ...(data.reservations?.ambiance ?? []),
        ...(data.reservations?.dressCode ?? []),
      ],
    };

    return payload;
  }

  updateBranchCoreFields(payload: {
    nameEn: string;
    nameAr: string;
    descriptionEn: string;
    descriptionAr: string;
    phone: string;
    address: string;
    openingHours: BranchHoursFormType;
    amenities: OptionType[];
    priceLevel?: number;
    longitude?: number;
    latitude?: number;
  }) {
    runInAction(() => {
      if (!this.branch) {
        this.branch = {
          details: this.defaultDetails,
          businessHours: this.defaultBusinessHours,
          reservations: this.defaultReservations,
          amenities: this.defaultAmenities,
          media: this.defaultMedia,
        };
      }

      const parsed = parseE164(payload.phone || "");
      const fallbackPhoneCode = getDefaultPhoneCode();

      this.branch.details = {
        ...this.branch.details,
        nameEn: payload.nameEn,
        nameAr: payload.nameAr,
        descriptionEn: payload.descriptionEn,
        descriptionAr: payload.descriptionAr,
        phone: payload.phone,
        phoneCode: parsed?.phoneCode ?? fallbackPhoneCode,
        phoneNumber: parsed?.phoneNumber ?? "",
        address: payload.address,
        longitude: payload.longitude,
        latitude: payload.latitude,
      };

      const normalizedSchedules = payload.openingHours.schedules.map(
        schedule => {
          if (!schedule.closed) {
            return {
              ...schedule,
              open: this.shiftTime(schedule.open, 3),
              close: this.shiftTime(schedule.close, 3),
            };
          }
          const defaultSchedule = this.defaultBusinessHours.schedules.find(
            d => d.day === schedule.day
          );
          return {
            ...schedule,
            open: defaultSchedule?.open ?? schedule.open,
            close: defaultSchedule?.close ?? schedule.close,
          };
        }
      );

      this.branch.businessHours = {
        ...this.branch.businessHours,
        schedules: normalizedSchedules,
      };

      const parkingList = payload.amenities.filter(item =>
        item.name.startsWith("parking")
      );
      const otherList = payload.amenities.filter(
        item => !item.name.startsWith("parking")
      );

      const idsByCategory = {
        amenities: otherList.map(item => item.id),
        parking: parkingList.map(item => item.id),
      };

      this.branch.amenities = idsByCategory;
      this._amenities = idsByCategory;

      if (!this._reservations) {
        this._reservations = this.defaultReservations;
      }

      const normalizedPriceLevel =
        payload.priceLevel ?? this.defaultReservations.priceLevel;

      this._reservations = {
        ...this._reservations,
        priceLevel: normalizedPriceLevel,
      };

      this._details = this.branch.details;
      this._businessHours = this.branch.businessHours;
    });
  }

  clearStore() {
    this._details = null;
    this._businessHours = null;
    this._reservations = null;

    this._media = null;
    this._amenities = null;
    this.branch = null;
    this.isInitialized = false;
    this.active = true;
  }
}
